import {printoutPalette} from "../../core/printoutPalette";
import {parseColorAttribute} from "../shared/blockAttributes";

// Persisted Tiptap node names; keep them stable.
export const PRINTOUT_TABLE_NODE_NAME = "table";
export const PRINTOUT_TABLE_ROW_NODE_NAME = "tableRow";
export const PRINTOUT_TABLE_CELL_NODE_NAME = "tableCell";
export const PRINTOUT_TABLE_HEADER_NODE_NAME = "tableHeader";

export const MIN_TABLE_COLUMNS = 1;
export const MAX_TABLE_COLUMNS = 10;
export const MIN_TABLE_ROWS = 1;
export const MAX_TABLE_ROWS = 50;

export const MIN_COLUMN_WIDTH_PX = 48;

export const DEFAULT_TABLE_BORDER_COLOR = printoutPalette.border;

export interface PrintoutTableNodeAttrs {
    backgroundColor?: string | null;
    borderColor?: string | null;
    borderTopColor?: string | null;
    borderRightColor?: string | null;
    borderBottomColor?: string | null;
    borderLeftColor?: string | null;
    colWidths?: number[] | null;
}

export interface PrintoutTableBorderColors {
    top: string;
    right: string;
    bottom: string;
    left: string;
}

// Directional colors fall back to the legacy shared color for stored tables.
export const resolveTableBorderColors = (
    attrs: PrintoutTableNodeAttrs,
): PrintoutTableBorderColors => {
    const fallback = parseColorAttribute(attrs.borderColor) ?? DEFAULT_TABLE_BORDER_COLOR;

    return {
        top: parseColorAttribute(attrs.borderTopColor) ?? fallback,
        right: parseColorAttribute(attrs.borderRightColor) ?? fallback,
        bottom: parseColorAttribute(attrs.borderBottomColor) ?? fallback,
        left: parseColorAttribute(attrs.borderLeftColor) ?? fallback,
    };
};

const clampInteger = (value: unknown, fallback: number, min: number, max: number): number => {
    const numberValue = typeof value === "number" ? value : Number(value);

    if (!Number.isFinite(numberValue)) return fallback;

    return Math.min(max, Math.max(min, Math.round(numberValue)));
};

export const clampTableColumns = (value: unknown, fallback: number) => (
    clampInteger(value, fallback, MIN_TABLE_COLUMNS, MAX_TABLE_COLUMNS)
);

export const clampTableRows = (value: unknown, fallback: number) => (
    clampInteger(value, fallback, MIN_TABLE_ROWS, MAX_TABLE_ROWS)
);

export const getEvenColumnWidths = (columns: number): number[] => {
    const safeColumns = Math.max(1, columns);

    return Array.from({length: safeColumns}, () => 1 / safeColumns);
};

// Invalid width arrays fall back to even fractions that sum to one.
export const normalizeColumnWidths = (widths: unknown, columns: number): number[] => {
    const safeColumns = Math.max(1, columns);
    const source = Array.isArray(widths)
        ? widths.map(Number).filter((value) => Number.isFinite(value) && value > 0)
        : [];

    if (source.length !== safeColumns) return getEvenColumnWidths(safeColumns);

    const total = source.reduce((sum, value) => sum + value, 0);
    if (total <= 0) return getEvenColumnWidths(safeColumns);

    return source.map((value) => value / total);
};

// New columns take an even share while existing columns retain their relative proportions.
export const insertColumnWidth = (
    widths: unknown,
    columns: number,
    index: number,
): number[] => {
    const safeColumns = Math.max(1, columns);
    const nextColumns = safeColumns + 1;
    const scale = safeColumns / nextColumns;
    const nextWidths = normalizeColumnWidths(widths, safeColumns).map((width) => width * scale);

    nextWidths.splice(Math.min(Math.max(index, 0), safeColumns), 0, 1 / nextColumns);

    return normalizeColumnWidths(nextWidths, nextColumns);
};

export const removeColumnWidth = (
    widths: unknown,
    columns: number,
    index: number,
): number[] => {
    const safeColumns = Math.max(1, columns);

    if (safeColumns <= 1) return getEvenColumnWidths(1);

    const nextWidths = normalizeColumnWidths(widths, safeColumns);

    nextWidths.splice(Math.min(Math.max(index, 0), safeColumns - 1), 1);

    return normalizeColumnWidths(nextWidths, safeColumns - 1);
};
